'use client';

import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { Facebook, Instagram, Youtube, Mail, MapPin, Phone } from 'lucide-react';
import { SUPPORT_EMAIL } from '@/lib/supportEmail';
import { AztangofestLogo } from './AztangofestLogo';

export function Footer() {
  const t = useTranslations('strings.footer');
  const year = new Date().getFullYear();

  return (
    <footer className="mt-auto shrink-0 bg-fest-void text-white">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <div className="mb-4">
              <AztangofestLogo height="30px" variant="dark" />
            </div>
            <p className="text-gray-400 text-sm mb-4">{t('tagline')}</p>
            <div className="text-sm text-gray-400">
              <p className="font-semibold text-white mb-1">{t('datesVenue')}</p>
              <p>{t('venueLine')}</p>
              <p>{t('cityCountry')}</p>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-4 text-fest-gold">{t('sectionFestival')}</h3>
            <ul className="space-y-2 text-sm">
              {[
                { href: '/', key: 'linkAbout' as const },
                { href: '/#program', key: 'linkSchedule' as const },
                { href: '/#teachers', key: 'linkTeachers' as const },
                { href: '/#orchestra', key: 'linkOrchestras' as const },
                { href: '/#concerts', key: 'linkConcerts' as const },
                { href: '/#location', key: 'linkLocation' as const },
              ].map(({ href, key }) => (
                <li key={href}>
                  <Link href={href} className="text-gray-400 hover:text-fest-gold transition-colors">
                    {t(key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4 text-fest-gold">{t('sectionParticipation')}</h3>
            <ul className="space-y-2 text-sm">
              {[
                { href: '/#participation-packages', key: 'linkTickets' as const },
                { href: '/registration-flow', key: 'linkRegistration' as const },
                { href: '/legal', key: 'linkRules' as const },
                { href: '/legal', key: 'linkDocuments' as const },
              ].map(({ href, key }, i) => (
                <li key={`${href}-${i}`}>
                  <Link href={href} className="text-gray-400 hover:text-fest-gold transition-colors">
                    {t(key)}
                  </Link>
                </li>
              ))}
              <li>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gray-400 hover:text-fest-gold transition-colors">
                  {t('linkContact')}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4 text-fest-gold">{t('sectionContacts')}</h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-fest-gold" />
                <span>{t('addressLine')}</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 flex-shrink-0 text-fest-gold" />
                <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-fest-gold transition-colors">
                  {SUPPORT_EMAIL}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 flex-shrink-0 text-fest-gold" />
                <a href="tel:+994101054321" className="hover:text-fest-gold transition-colors">
                  +994 (10) 105 43 21
                </a>
              </li>
            </ul>

            <div className="mt-6">
              <h4 className="font-semibold mb-3 text-sm text-fest-gold">{t('followUs')}</h4>
              <div className="flex items-center gap-3">
                {[
                  { icon: Facebook, href: 'https://facebook.com', labelKey: 'ariaFacebook' as const },
                  { icon: Instagram, href: 'https://www.instagram.com/aztangofest/', labelKey: 'ariaInstagram' as const },
                  { icon: Youtube, href: 'https://youtube.com', labelKey: 'ariaYoutube' as const },
                ].map(({ icon: Icon, href, labelKey }) => (
                  <a
                    key={labelKey}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-fest-gold transition-colors"
                    aria-label={t(labelKey)}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
          <p>{t('copyright', { year })}</p>
          <p className="text-xs">
            {t('organizer')}{' '}
            <span className="text-fest-gold">{t('organizerName')}</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
